// httpPorts — the IAM BC slice: the auth probe (`/auth/me`) the host projects
// into `authStatus`, and the Auth0 hand-off the sign-in surface fires.

import { type LoginHint, loginHintParam } from "@bilda/app/loginHint";
import {
    type SignInProvider,
    signInProviderParam,
} from "@bilda/app/signInProvider";
import { Result } from "@bilda/utils";
import { mkMalformed } from "../externalError.js";
import {
    type AuthProbe,
    deleteAccount,
    getAuthMe,
    getWorkspaceMembers,
    getWorkspaces,
    inviteWorkspaceMember,
    logout,
    removeWorkspaceMember,
} from "../httpClient.js";
import type {
    AuthStatus,
    IamPorts,
    WorkspaceMember,
    WorkspaceRole,
    WorkspaceSummary,
} from "../ports/iam.js";
import type { PortResult } from "../ports/result.js";
import { fields, list, str } from "../wireShape.js";

// Project the raw HTTP probe into the domain `AuthStatus`. The `authed` arm
// parses the user out of the envelope value (dropping a row missing an id/email
// to `anonymous`, the same forward-compat posture as every other list read —
// an unparseable identity is not a signed-in author).
export const projectProbe = (probe: AuthProbe): AuthStatus => {
    switch (probe._tag) {
        case "anonymous":
            return { _tag: "anonymous" };
        case "authed": {
            const f = fields(probe.value);
            const id = str(f?.get("userId"));
            const email = str(f?.get("email"));
            if (id === undefined || email === undefined) {
                return { _tag: "anonymous" };
            }
            // Absent or non-boolean `isAdmin` reads as not admin.
            const isAdmin = f?.get("isAdmin") === true;
            return { _tag: "authenticated", user: { id, email, isAdmin } };
        }
    }
};

const loadAuthStatus = async (): PortResult<AuthStatus> => {
    const r = await getAuthMe();
    return Result.isErr(r) ? r : Result.ok(projectProbe(r.value));
};

// A full-page navigation to the server's OIDC login route, which redirects
// to Auth0. Lives in the port (the impure shell), never in a component —
// the sign-in surface only maps the click intent to this call.
const beginExternalLoginPort = (loginHint?: LoginHint): void => {
    const query =
        loginHint === undefined
            ? ""
            : `?${new URLSearchParams({ [loginHintParam]: loginHint })}`;
    window.location.assign(`/v1/auth/oidc/login${query}`);
};

const beginProviderLoginPort = (provider: SignInProvider): void => {
    const query = new URLSearchParams({ [signInProviderParam]: provider });
    window.location.assign(`/v1/auth/oidc/login?${query}`);
};

const signOutPort = async (): PortResult<{ readonly loggedOut: true }> => {
    const r = await logout();
    return Result.isErr(r) ? r : Result.ok({ loggedOut: true });
};

// The live vocabulary is exactly "owner" | "member" (packages/server/src/
// auth/members.ts) — a role string outside that set degrades to "member"
// rather than crash, the same forward-compat posture as every other list
// read here.
const asRole = (v: unknown): WorkspaceRole =>
    v === "owner" ? "owner" : "member";

const parseWorkspace = (v: unknown): WorkspaceSummary | undefined => {
    const f = fields(v);
    const id = str(f?.get("workspaceId"));
    return id === undefined
        ? undefined
        : {
              id,
              role: asRole(f?.get("role")),
              // "" when unnamed (born so, or an older server without the
              // column) — the caller derives a display label from empty.
              name: str(f?.get("name")) ?? "",
          };
};

const parseMember = (v: unknown): WorkspaceMember | undefined => {
    const f = fields(v);
    const userId = str(f?.get("userId"));
    const email = str(f?.get("email"));
    return userId === undefined || email === undefined
        ? undefined
        : { userId, email, role: asRole(f?.get("role")) };
};

const listWorkspacesPort = async (): PortResult<
    readonly WorkspaceSummary[]
> => {
    const r = await getWorkspaces();
    if (Result.isErr(r)) return r;
    const workspaces = list(fields(r.value)?.get("workspaces"));
    return Result.ok(
        workspaces
            .map(parseWorkspace)
            .filter((w): w is WorkspaceSummary => w !== undefined),
    );
};

const listWorkspaceMembersPort = async (
    workspaceId: string,
): PortResult<readonly WorkspaceMember[]> => {
    const r = await getWorkspaceMembers(workspaceId);
    if (Result.isErr(r)) return r;
    const members = list(fields(r.value)?.get("members"));
    return Result.ok(
        members
            .map(parseMember)
            .filter((m): m is WorkspaceMember => m !== undefined),
    );
};

const inviteWorkspaceMemberPort = async (
    workspaceId: string,
    email: string,
): PortResult<WorkspaceMember> => {
    const r = await inviteWorkspaceMember(workspaceId, email);
    if (Result.isErr(r)) return r;
    const member = parseMember(r.value);
    return member === undefined
        ? Result.err(mkMalformed("invite response missing userId/email"))
        : Result.ok(member);
};

const removeWorkspaceMemberPort = async (
    workspaceId: string,
    userId: string,
): PortResult<{ readonly removed: true }> => {
    const r = await removeWorkspaceMember(workspaceId, userId);
    return Result.isErr(r) ? r : Result.ok({ removed: true });
};

const deleteAccountPort = async (): PortResult<{ readonly deleted: true }> => {
    const r = await deleteAccount();
    return Result.isErr(r) ? r : Result.ok({ deleted: true });
};

export const iamHttp: IamPorts = {
    loadAuthStatus,
    beginExternalLogin: beginExternalLoginPort,
    beginProviderLogin: beginProviderLoginPort,
    signOut: signOutPort,
    listWorkspaces: listWorkspacesPort,
    listWorkspaceMembers: listWorkspaceMembersPort,
    inviteWorkspaceMember: inviteWorkspaceMemberPort,
    removeWorkspaceMember: removeWorkspaceMemberPort,
    deleteAccount: deleteAccountPort,
};
